import { useTranslation } from 'react-i18next';
import { cn } from '../lib/utils';
import { LANGUAGE_OPTIONS, changeAppLanguage, currentLanguage } from '../lib/language';

/**
 * Two-way language picker rendered as a pill of flags with the active one
 * ringed — the same shape as the ThemeSwitcher it sits next to. Writes through
 * `changeAppLanguage`, so the choice also lands on the account.
 */
export function LanguagePicker({ className }: { className?: string }) {
  const { t, i18n } = useTranslation();
  const language = currentLanguage(i18n.language);

  // A single indicator that slides between the options, rather than a border
  // toggled on each button: that is what makes the change animate.
  const activeIndex = Math.max(
    LANGUAGE_OPTIONS.findIndex((option) => option.value === language),
    0
  );

  return (
    <div
      role="radiogroup"
      aria-label={t('language.label')}
      className={cn(
        'relative inline-flex items-center rounded-full bg-background ring-1 ring-inset ring-border',
        className
      )}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute left-0 top-0 size-8 rounded-full border border-foreground/40 transition-transform duration-300 ease-out"
        style={{ transform: `translateX(${activeIndex * 100}%)` }}
      />

      {LANGUAGE_OPTIONS.map(({ value, flag, label }) => {
        const isActive = value === language;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={label}
            title={label}
            data-active={isActive}
            onClick={() => void changeAppLanguage(value)}
            className="relative flex size-8 items-center justify-center rounded-full text-sm leading-none opacity-60 transition-opacity hover:opacity-100 data-[active=true]:opacity-100"
          >
            <span aria-hidden>{flag}</span>
          </button>
        );
      })}
    </div>
  );
}
